import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, CalendarClock, ShieldCheck } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
    "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ParticleCanvas = () => {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas.getContext("2d");
        let raf;
        let t = 0;
        const mouse = { x: -9999, y: -9999 };

        const resize = () => {
            canvas.width = canvas.offsetWidth * window.devicePixelRatio;
            canvas.height = canvas.offsetHeight * window.devicePixelRatio;
        };
        resize();
        window.addEventListener("resize", resize);

        const onMove = (e) => {
            const r = canvas.getBoundingClientRect();
            mouse.x = (e.clientX - r.left) * window.devicePixelRatio;
            mouse.y = (e.clientY - r.top) * window.devicePixelRatio;
        };
        window.addEventListener("mousemove", onMove);

        const draw = () => {
            t += 0.008;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const gap = 44 * window.devicePixelRatio;
            for (let x = gap / 2; x < canvas.width; x += gap) {
                for (let y = gap / 2; y < canvas.height; y += gap) {
                    const wave = Math.sin(t + x * 0.004 + y * 0.006) * 0.5 + 0.5;
                    const dx = x - mouse.x;
                    const dy = y - mouse.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    const near = Math.max(0, 1 - dist / (220 * window.devicePixelRatio));
                    const alpha = 0.04 + wave * 0.06 + near * 0.55;
                    const size = (1 + near * 1.6) * window.devicePixelRatio;
                    ctx.beginPath();
                    ctx.arc(x, y, size, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(0, 240, 255, ${alpha})`;
                    ctx.fill();
                }
            }
            raf = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
            window.removeEventListener("mousemove", onMove);
        };
    }, []);

    return <canvas ref={ref} className="absolute inset-0 h-full w-full" aria-hidden="true" />;
};

const LINES = ["Transforming", "Businesses with", "Seamless AI."];

export const Hero = () => {
    const sectionRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 120, damping: 18 });

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    const onMouseMove = (e) => {
        const r = sectionRef.current.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="hero"
            ref={sectionRef}
            onMouseMove={onMouseMove}
            data-testid="hero-section"
            className="grain relative flex min-h-screen items-center overflow-hidden pt-[72px]"
        >
            <ParticleCanvas />
            <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-volt/10 blur-[140px]" />
            <div className="pointer-events-none absolute -right-40 bottom-0 h-[420px] w-[420px] rounded-full bg-blue-600/10 blur-[140px]" />

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto grid w-full max-w-7xl gap-14 px-5 py-20 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 backdrop-blur-md"
                        data-testid="hero-status-pill"
                    >
                        <span className="animate-pulse-dot h-2 w-2 rounded-full bg-emerald-400" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">
                            Systems Operational, HQ: Bengaluru India
                        </span>
                    </motion.div>

                    <h1 className="font-syne text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-[4.2rem]" data-testid="hero-headline">
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${i === 2 ? "text-glow bg-gradient-to-r from-volt to-blue-500 bg-clip-text text-transparent" : ""}`}
                                    initial={{ y: "115%" }}
                                    animate={{ y: "0%" }}
                                    transition={{ duration: 0.95, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
                        data-testid="hero-subcopy"
                    >
                        Syntera Technologies builds post-quantum security software,
                        finding vulnerable cryptography, scoring the risk and planning
                        the migration to quantum-safe algorithms. Engineered in Bengaluru.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-explore-btn"
                            onClick={() => scrollToId("#capabilities")}
                            className="group flex items-center gap-2.5 rounded-full bg-volt px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-[#07090E] transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.45)]"
                        >
                            Explore Capabilities
                            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-book-call-btn"
                            onClick={() => scrollToId("#contact")}
                            className="group flex items-center gap-2.5 rounded-full border border-white/15 px-7 py-3.5 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:border-volt/60 hover:text-volt"
                        >
                            <CalendarClock size={15} />
                            Book a Strategy Call
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="relative hidden lg:block"
                    style={{ perspective: 1200 }}
                >
                    <motion.div
                        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                        className="relative"
                        data-testid="hero-visual"
                    >
                        <div className="overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_rgba(0,0,0,0.6)]">
                            <motion.img
                                src={HERO_IMG}
                                alt="Syntera AI data center infrastructure"
                                style={{ y: imgY }}
                                className="h-[460px] w-full scale-110 object-cover"
                                data-testid="hero-image"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#07090E]/80 via-transparent to-transparent" />
                        </div>
                        <motion.div
                            style={{ transform: "translateZ(60px)" }}
                            className="absolute -bottom-6 -left-8 flex items-center gap-3 rounded-xl border border-white/10 bg-[#0D111A]/90 px-5 py-4 backdrop-blur-xl"
                            data-testid="hero-metric-card"
                        >
                            <ShieldCheck className="text-volt" size={22} />
                            <div>
                                <div className="font-syne text-lg font-bold text-white">847 Detection Patterns</div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Post-quantum readiness, built in
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
            >
                <div className="flex h-10 w-6 items-start justify-center rounded-full border border-white/20 p-1.5">
                    <motion.div
                        animate={{ y: [0, 14, 0] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                        className="h-2 w-1 rounded-full bg-volt"
                    />
                </div>
            </motion.div>
        </section>
    );
};
